// What the Layout Canvas tells the estimator at a glance — pure, so it can be tested without a canvas:
//   * the status bar's words (current mode, cursor position in feet-inches, scale chip, drawing counts)
//   * the full-width crosshair's look (off / standard / bold; light and dark drawing views) and the viewer's
//     remembered choice
//   * the running quantity shown beside the cursor while a run or area is being traced
// Nothing here is stored with the estimate; the crosshair choice is a per-viewer convenience (localStorage,
// always under try/catch — storage can be blocked or empty).

import { polygonArea, roomVertices } from "./takeoffGeometry";
import { fmtLength, fmtQty } from "./units";

/** 83.4 ft -> `83' 5"` (to the nearest inch; the sign is kept). Matches how a dimension is printed on a drawing. */
export function fmtFeetInches(ft) {
  if (!Number.isFinite(ft)) return "—";
  const sign = ft < 0 ? "-" : "";
  const totalIn = Math.round(Math.abs(ft) * 12);
  return `${sign}${Math.floor(totalIn / 12)}' ${totalIn % 12}"`;
}

/** The cursor readout: `x 83' 5" · y 5' 7"` (metric: `x 25.43 m · y 1.70 m`). */
export const cursorText = (pt, units = "imperial") => (pt && Number.isFinite(pt.x) && Number.isFinite(pt.y) ? `x ${fmtLength(pt.x, units)} · y ${fmtLength(pt.y, units)}` : "—");

// Which tool is armed, in the words the status bar shows. Order matters: the first armed one wins.
const MODE_ORDER = [
  ["sweep", "count by example"], ["calib", "set scale"], ["imgMode", "move drawing"], ["measure", "measure"],
  ["cond", "trace condition"], ["area", "trace area"], ["route", "trace route"], ["backOut", "back-out"],
  ["symbol", "place symbol"], ["draw", "draw room"],
];
export function modeLabel(flags = {}) {
  const hit = MODE_ORDER.find(([k]) => flags[k]);
  return hit ? hit[1] : "select";
}

/**
 * The scale chip: which scale is set on this drawing, in the picker's own words.
 *   settings          the sheet ({ ImageFtPerInch } for a PDF, { ImageScaleFtPerPx } for an image)
 *   knownScales       [{ label, ftPerInch }] — the picker's architectural + engineering lists
 * -> { set: boolean, text: string }
 */
export function scaleChip({ isPdf, settings, knownScales = [] } = {}) {
  if (isPdf) {
    const v = +settings?.ImageFtPerInch;
    if (!(v > 0)) return { set: false, text: "Set scale…" };
    const hit = knownScales.find(s => Math.abs(s.ftPerInch - v) / v < 0.005);
    return { set: true, text: hit ? hit.label : `${Math.round(v * 1000) / 1000} ft per inch` };
  }
  const v = +settings?.ImageScaleFtPerPx;
  if (!(v > 0)) return { set: false, text: "Set scale…" };
  return { set: true, text: `${Math.round(v * 10000) / 10000} ft per px` };
}

// ── crosshair ────────────────────────────────────────────────────────────────
export const CROSSHAIR_STYLES = Object.freeze(["bold", "standard", "precision", "off"]);
export const CROSSHAIR_STORAGE_KEY = "cs.takeoff.crosshair";
export const CROSSHAIR_LABELS = Object.freeze({ bold: "Bold", standard: "Standard", precision: "Precision", off: "Off" });

/**
 * How the full-width crosshair is drawn. A solid coloured line over a contrasting halo, so it reads on top of
 * black linework and on a dark drawing alike, with a clear gap around the exact point being picked.
 * Screen pixels; null when it is off.
 */
export function crosshairSpec(style, dark = false, tuning = null) {
  if (style === "off") return null;
  const bold = style !== "standard";
  const t = normalizeCrosshairTuning(tuning);
  const lineWidth = t.width ?? (bold ? 2 : 1.25);
  return {
    line: dark ? "#8fa8ff" : "#3b4fd8",
    lineWidth,
    lineOpacity: t.opacity ?? (bold ? 0.95 : 0.75),
    halo: dark ? "#0b1220" : "#ffffff",
    haloWidth: lineWidth + 3,
    haloOpacity: bold ? 0.7 : 0.5,
    gap: bold ? 9 : 7,       // empty space around the pick point
    ring: bold ? 7 : 5,      // the ring marking it
    arm: style === "precision" ? t.arm : null,   // Precision cursor: short arms (screen px) instead of full-width lines
  };
}

// Per-viewer fine tuning of the crosshair: line width (px), opacity, and the Precision cursor's arm length (px).
// null = use the style's own value. Display only; screen-space, so it does not change with zoom.
export const CROSSHAIR_TUNING_LIMITS = Object.freeze({ width: [1, 4], opacity: [0.3, 1], arm: [30, 200] });
export const DEFAULT_CROSSHAIR_TUNING = Object.freeze({ width: null, opacity: null, arm: 80 });
export const CROSSHAIR_TUNING_STORAGE_KEY = "cs.takeoff.crosshair.tuning";
export function normalizeCrosshairTuning(raw) {
  const out = { ...DEFAULT_CROSSHAIR_TUNING };
  if (!raw || typeof raw !== "object") return out;
  for (const k of Object.keys(CROSSHAIR_TUNING_LIMITS)) {
    const v = raw[k];
    if (v === null || v === undefined || v === "") continue;
    const n = Number(v);
    if (!Number.isFinite(n)) continue;
    const [lo, hi] = CROSSHAIR_TUNING_LIMITS[k];
    out[k] = Math.min(hi, Math.max(lo, n));
  }
  return out;
}
export function readCrosshairTuning(storage = typeof localStorage !== "undefined" ? localStorage : null) {
  try { return normalizeCrosshairTuning(JSON.parse(storage?.getItem(CROSSHAIR_TUNING_STORAGE_KEY) || "null")); } catch { return { ...DEFAULT_CROSSHAIR_TUNING }; }
}
export function writeCrosshairTuning(tuning, storage = typeof localStorage !== "undefined" ? localStorage : null) {
  try { storage?.setItem(CROSSHAIR_TUNING_STORAGE_KEY, JSON.stringify(normalizeCrosshairTuning(tuning))); } catch { /* not remembered */ }
}

export const nextCrosshairStyle = (style) => CROSSHAIR_STYLES[(CROSSHAIR_STYLES.indexOf(style) + 1) % CROSSHAIR_STYLES.length];

export function readCrosshairStyle(storage = typeof localStorage !== "undefined" ? localStorage : null) {
  try { const v = storage?.getItem(CROSSHAIR_STORAGE_KEY); return CROSSHAIR_STYLES.includes(v) ? v : "bold"; } catch { return "bold"; }
}
export function writeCrosshairStyle(style, storage = typeof localStorage !== "undefined" ? localStorage : null) {
  try { storage?.setItem(CROSSHAIR_STORAGE_KEY, style); } catch { /* storage unavailable — the choice just isn't remembered */ }
}

// ── running quantity while tracing ───────────────────────────────────────────
/**
 * The measure of what is being traced, including the segment out to the cursor.
 *   kind 'linear' -> length in feet ('LF');   kind 'area' -> area in square feet ('SF', needs 3+ points)
 * -> { value, unit } | null
 */
export function runningQuantity(kind, pts = [], cursor = null) {
  const all = cursor ? [...pts, cursor] : [...pts];
  if (kind === "area") return all.length >= 3 ? { value: polygonArea(all), unit: "SF" } : null;
  if (all.length < 2) return null;
  let len = 0;
  for (let i = 1; i < all.length; i++) len += Math.hypot(all[i].x - all[i - 1].x, all[i].y - all[i - 1].y);
  return { value: len, unit: "LF" };
}

export const formatQuantity = (q, units = "imperial") => {
  if (!q) return "";
  return units === "metric" ? fmtQty({ q: q.value, u: q.unit }, units) : `${(Math.round(q.value * 10) / 10).toLocaleString()} ${q.unit}`;
};

// ── crosshair layers ─────────────────────────────────────────────────────────
// What the crosshair shows beyond its lines. Each is a separate per-viewer switch; the default is the layers that
// give information the plan itself does not: where a click will actually land, and how far a snap can reach.
export const CROSSHAIR_LAYERS = Object.freeze([
  { key: "ring", label: "Snap-reach ring", hint: "The ring's size is how far a click can snap — it follows zoom." },
  { key: "toolColor", label: "Tool colour", hint: "The crosshair takes the colour of the condition or tool in use." },
  { key: "snapPreview", label: "Snap preview", hint: "Marks the point a click will land on, names what it snapped to, and draws dashed alignment lines." },
  { key: "angleGuide", label: "Angle guide", hint: "While tracing: the locked angle as an arc from the last point, with a guide ray." },
  { key: "readout", label: "X / Y / Δ box", hint: "A small box by the cursor with its position and the distance from the last point." },
  { key: "magnetic", label: "Nearby snap points", hint: "Dots on the nearest corners and wall ends that a click could snap to." },
]);
export const DEFAULT_CROSSHAIR_LAYERS = Object.freeze({ ring: true, toolColor: true, snapPreview: true, angleGuide: true, readout: false, magnetic: false });
export const CROSSHAIR_LAYERS_STORAGE_KEY = "cs.takeoff.crosshair.layers";

export function readCrosshairLayers(storage = typeof localStorage !== "undefined" ? localStorage : null) {
  try {
    const raw = JSON.parse(storage?.getItem(CROSSHAIR_LAYERS_STORAGE_KEY) || "null");
    if (!raw || typeof raw !== "object") return { ...DEFAULT_CROSSHAIR_LAYERS };
    const out = { ...DEFAULT_CROSSHAIR_LAYERS };
    for (const k of Object.keys(out)) if (typeof raw[k] === "boolean") out[k] = raw[k];
    return out;
  } catch { return { ...DEFAULT_CROSSHAIR_LAYERS }; }
}
export function writeCrosshairLayers(layers, storage = typeof localStorage !== "undefined" ? localStorage : null) {
  try { storage?.setItem(CROSSHAIR_LAYERS_STORAGE_KEY, JSON.stringify(layers)); } catch { /* storage unavailable — not remembered */ }
}

/** Words for what snapDrawnPoint snapped to. */
const SNAP_LABELS = { "corner": "Room corner", "wall-end": "Wall end", "wall-corner": "Wall corner", "wall-line": "On wall line" };
export const snapKindLabel = (kind) => SNAP_LABELS[kind] || "";

/**
 * The crosshair's colour: the condition / tool in use. `colors` carries the picked types' own colours; a tool with no
 * colour of its own gets a fixed one. null = no tool-specific colour (use the style's default).
 */
export function crosshairToolColor({ mode, colors = {} } = {}) {
  const fixed = { "measure": "#dc2626", "set scale": "#d97706", "draw room": "#2563eb", "count by example": "#2563eb", "back-out": "#dc2626", "move drawing": "#7c3aed" };
  const own = { "trace condition": colors.cond, "trace area": colors.area, "trace route": colors.route, "place symbol": colors.symbol };
  return own[mode] || fixed[mode] || null;
}

/**
 * The nearest snap points a click could land on (room corners, wall ends, wall-line crossings), nearest first, at most
 * `max`, all within `radiusFt` of the pointer. The same targets snapDrawnPoint considers, listed for display.
 */
export function nearbySnapTargets(pt, rooms = [], wallSegments = null, radiusFt, max = 12) {
  if (!pt || !(radiusFt > 0)) return [];
  const out = [];
  const add = (x, y, kind) => { const d = Math.hypot(pt.x - x, pt.y - y); if (d <= radiusFt) out.push({ x, y, kind, d }); };
  for (const r of rooms) for (const v of roomVertices(r) || []) add(v.x, v.y, "corner");
  const hs = wallSegments?.hSegs || [], vs = wallSegments?.vSegs || [];
  for (const h of hs) if (Math.abs(h.y - pt.y) <= radiusFt) { add(h.x0, h.y, "wall-end"); add(h.x1, h.y, "wall-end"); }
  for (const v of vs) if (Math.abs(v.x - pt.x) <= radiusFt) { add(v.x, v.y0, "wall-end"); add(v.x, v.y1, "wall-end"); }
  for (const v of vs) for (const h of hs) add(v.x, h.y, "wall-corner");
  out.sort((a, b) => a.d - b.d);
  // one dot per place
  const kept = [];
  for (const o of out) if (kept.every(k => Math.hypot(k.x - o.x, k.y - o.y) > 1e-6)) kept.push(o);
  return kept.slice(0, max).map(({ x, y, kind }) => ({ x, y, kind }));
}

/** Math angle (degrees, 0 = +x, counter-clockwise on screen) of the segment from `prev` to `cursor`, 0..360. */
export function segmentAngleDeg(prev, cursor) {
  const a = (Math.atan2(-(cursor.y - prev.y), cursor.x - prev.x) * 180) / Math.PI;
  return ((a % 360) + 360) % 360;
}
export const isOrthoAngle = (deg) => { const m = ((deg % 90) + 90) % 90; return Math.min(m, 90 - m) < 0.01; };

/**
 * The angle guide's arc in SCREEN pixels: from the 0° reference to the segment's angle, radius rPx around the last point.
 * -> { d (SVG path), label: {x, y}, text, ortho }
 */
export function angleArc(prevScreen, angleDeg, rPx = 28) {
  const a = angleDeg > 180 ? angleDeg - 360 : angleDeg; // -180..180: always the short way round
  const rad = (a * Math.PI) / 180;
  const sx = prevScreen.x + rPx, sy = prevScreen.y;
  const ex = prevScreen.x + rPx * Math.cos(rad), ey = prevScreen.y - rPx * Math.sin(rad);
  const mid = rad / 2;
  return {
    d: `M ${sx} ${sy} A ${rPx} ${rPx} 0 0 ${a >= 0 ? 0 : 1} ${ex} ${ey}`,
    label: { x: prevScreen.x + (rPx + 14) * Math.cos(mid), y: prevScreen.y - (rPx + 14) * Math.sin(mid) },
    text: `${Math.round(Math.abs(a) * 10) / 10}°`,
    ortho: isOrthoAngle(angleDeg),
  };
}
